"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { usePathname, useSearchParams } from "next/navigation";

import type { GamDisplaySize } from "@/components/ads/config";

import { AD_ARIA_LABEL } from "./config";
import styles from "./GamDisplaySlot.module.css";

type GamSize = GamDisplaySize;

type TargetingValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>;

type GamResponseInfo = {
  advertiserId?: number | null;
  campaignId?: number | null;
  creativeId?: number | string | null;
  lineItemId?: number | null;
  sourceAgnosticCreativeId?: number | null;
  sourceAgnosticLineItemId?: number | null;
};

type SlotRenderEndedEvent = {
  slot: unknown;
  isEmpty: boolean;
  size?: [number, number] | string | null;
};

type GoogleTagListener = {
  bivarianceHack(_event: SlotRenderEndedEvent): void;
}["bivarianceHack"];

type GoogleTagSlot = {
  getSlotElementId: () => string;
  getAdUnitPath?: () => string;
  getResponseInformation?: () => GamResponseInfo | null;
  setTargeting: (_key: string, _value: string | string[]) => GoogleTagSlot;
  setConfig?: (_config: {
    targeting?: Record<string, string | string[]>;
  }) => GoogleTagSlot;
  addService: (_service: unknown) => GoogleTagSlot;
};

type GoogleTagPubAds = {
  getSlots: () => GoogleTagSlot[];
  collapseEmptyDivs: (_collapse?: boolean) => void;
  enableSingleRequest: () => void;
  addEventListener: (_eventName: string, _listener: GoogleTagListener) => void;
  removeEventListener: (_eventName: string, _listener: GoogleTagListener) => void;
};

type GoogleTag = {
  cmd: Array<() => void>;
  apiReady?: boolean;
  setConfig?: (_config: {
    collapseDiv?: "ON_NO_FILL" | "BEFORE_FETCH" | "DISABLED";
    singleRequest?: boolean;
  }) => void;
  defineSlot: (
    _adUnitPath: string,
    _sizes: GamSize[],
    _divId: string
  ) => GoogleTagSlot | null;
  display: (_divId: string) => void;
  destroySlots: (_slots?: GoogleTagSlot[]) => boolean;
  enableServices: () => void;
  pubads: () => GoogleTagPubAds;
};

export type GamDisplaySlotRenderInfo = {
  id: string;
  adUnit: string;
  instanceId: string;
  isEmpty: boolean;
  filled: boolean;
  size?: [number, number] | string | null;
  response: GamResponseInfo | null;
  isPremiumDemand: boolean;
  isFluidOrNative: boolean;
};

type GamDisplaySlotProps = {
  id: string;
  adUnit: string;
  sizes: GamSize[];
  className?: string;
  minHeight?: number | "auto";
  /**
   * When set, the GPT slot is only defined/displayed once this wrapper
   * approaches the viewport. This prevents below-the-fold base-page ads
   * from creating non-viewable impressions during client-side navigation.
   */
  lazyRootMargin?: string;
  targeting?: Record<string, TargetingValue | null | undefined>;
  onRenderEndedAction?: (_info: GamDisplaySlotRenderInfo) => void;
};

const IS_PROD = process.env.NODE_ENV === "production";

function normalizeTargetingValue(value: TargetingValue): string | string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }

  return String(value);
}

function hasPositiveValue(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value) && value > 0;
  if (typeof value === "string") return value.trim().length > 0 && value !== "0";
  return Boolean(value);
}

function isPremiumDemand(response: GamResponseInfo | null): boolean {
  if (!response) return false;

  return (
    hasPositiveValue(response.lineItemId) ||
    hasPositiveValue(response.campaignId) ||
    hasPositiveValue(response.advertiserId) ||
    hasPositiveValue(response.creativeId) ||
    hasPositiveValue(response.sourceAgnosticLineItemId) ||
    hasPositiveValue(response.sourceAgnosticCreativeId)
  );
}

function isFluidOrNativeSize(size: SlotRenderEndedEvent["size"]): boolean {
  return typeof size === "string" && size.toLowerCase() === "fluid";
}

function measureRenderedHeight(elementId: string): number | null {
  const element = document.getElementById(elementId);
  if (!element) return null;

  const rectHeight = element.getBoundingClientRect().height;
  const scrollHeight = element.scrollHeight;
  const height = Math.max(rectHeight, scrollHeight);

  if (!Number.isFinite(height) || height <= 0) return null;
  return Math.ceil(height);
}

export default function GamDisplaySlot({
  id,
  adUnit,
  sizes,
  className,
  minHeight = "auto",
  lazyRootMargin,
  targeting,
  onRenderEndedAction,
}: GamDisplaySlotProps) {
  const pathname = usePathname() ?? "";
  const searchParams = useSearchParams();
  const routeKey = `${pathname}?${searchParams?.toString() ?? ""}`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const slotRef = useRef<GoogleTagSlot | null>(null);
  const onRenderEndedRef = useRef(onRenderEndedAction);
  const [renderedHeight, setRenderedHeight] = useState<number | null>(null);

  useEffect(() => {
    onRenderEndedRef.current = onRenderEndedAction;
  }, [onRenderEndedAction]);

  const instanceId = useMemo(
    () => `${id}-${routeKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`,
    [id, routeKey]
  );

  const sizesKey = useMemo(() => JSON.stringify(sizes), [sizes]);
  const targetingKey = useMemo(() => JSON.stringify(targeting ?? {}), [targeting]);

  useEffect(() => {
    if (!IS_PROD) return undefined;
    if (!adUnit || sizes.length === 0) return undefined;

    window.googletag = window.googletag || { cmd: [] };
    const googletag = window.googletag as unknown as GoogleTag;

    let destroyed = false;
    let observer: IntersectionObserver | null = null;

    setRenderedHeight(null);

    const handleSlotRenderEnded: GoogleTagListener = (event) => {
      const slot = slotRef.current;
      if (!slot || event.slot !== slot) return;

      const response = slot.getResponseInformation?.() ?? null;
      const premium = isPremiumDemand(response);
      const fluidOrNative = isFluidOrNativeSize(event.size ?? null);

      const info: GamDisplaySlotRenderInfo = {
        id,
        adUnit: slot.getAdUnitPath?.() ?? adUnit,
        instanceId,
        isEmpty: event.isEmpty,
        filled: !event.isEmpty,
        size: event.size ?? null,
        response,
        isPremiumDemand: premium,
        isFluidOrNative: fluidOrNative,
      };

      window.dispatchEvent(
        new CustomEvent("bfc-gam-slot-render-ended", { detail: info })
      );
      onRenderEndedRef.current?.(info);

      if (minHeight === "auto") {
        return;
      }

      if (event.isEmpty) {
        setRenderedHeight(0);
        return;
      }

      if (Array.isArray(event.size)) {
        const height = Number(event.size[1]);

        if (Number.isFinite(height) && height >= 0) {
          setRenderedHeight(height);
        }

        return;
      }

      if (fluidOrNative) {
        window.setTimeout(() => {
          if (slotRef.current !== slot) return;

          const measuredHeight = measureRenderedHeight(instanceId);

          if (measuredHeight !== null) {
            setRenderedHeight(measuredHeight);
          }
        }, 250);
      }
    };

    const defineAndDisplay = () => {
      googletag.cmd.push(() => {
        if (destroyed || slotRef.current) return;

        const pubads = googletag.pubads();
        pubads.addEventListener("slotRenderEnded", handleSlotRenderEnded);

        const existingSlot = pubads
          .getSlots()
          .find((slot) => slot.getSlotElementId() === instanceId);

        if (existingSlot) {
          slotRef.current = existingSlot;
          googletag.display(instanceId);
          return;
        }

        const slot = googletag
          .defineSlot(adUnit, JSON.parse(sizesKey) as GamSize[], instanceId)
          ?.addService(pubads);

        if (!slot) return;

        const targetingObject = JSON.parse(targetingKey) as Record<
          string,
          TargetingValue | null | undefined
        >;

        const normalizedTargeting = Object.entries(targetingObject).reduce<
          Record<string, string | string[]>
        >((acc, [key, value]) => {
          if (value === null || value === undefined) return acc;

          const normalized = normalizeTargetingValue(value);
          if (Array.isArray(normalized) && normalized.length === 0) return acc;

          acc[key] = normalized;
          return acc;
        }, {});

        if (slot.setConfig) {
          slot.setConfig({ targeting: normalizedTargeting });
        } else {
          Object.entries(normalizedTargeting).forEach(([key, value]) => {
            slot.setTargeting(key, value);
          });
        }

        slotRef.current = slot;
        googletag.display(instanceId);
      });
    };

    if (lazyRootMargin && "IntersectionObserver" in window) {
      const root = rootRef.current;

      if (!root) {
        defineAndDisplay();
      } else {
        observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            observer?.disconnect();
            observer = null;
            defineAndDisplay();
          },
          { rootMargin: lazyRootMargin, threshold: 0 }
        );

        observer.observe(root);
      }
    } else {
      defineAndDisplay();
    }

    return () => {
      destroyed = true;

      observer?.disconnect();
      observer = null;

      const slot = slotRef.current;
      slotRef.current = null;

      if (!window.googletag?.cmd) return;

      const cleanupGoogletag = window.googletag as unknown as GoogleTag;

      cleanupGoogletag.cmd.push(() => {
        cleanupGoogletag
          .pubads()
          .removeEventListener("slotRenderEnded", handleSlotRenderEnded);

        if (slot) {
          cleanupGoogletag.destroySlots([slot]);
        }
      });
    };
  }, [
    adUnit,
    id,
    instanceId,
    sizes.length,
    sizesKey,
    targetingKey,
    minHeight,
    lazyRootMargin,
  ]);

  const effectiveMinHeight =
    renderedHeight === null
      ? minHeight
      : minHeight === "auto"
        ? renderedHeight
        : Math.max(Number(minHeight), renderedHeight);

  const effectiveMinHeightValue =
    effectiveMinHeight === "auto" ? "auto" : `${effectiveMinHeight}px`;

  const cssVars: CSSProperties & Record<"--bfc-gam-min-height", string> = {
    "--bfc-gam-min-height": effectiveMinHeightValue,
  };

  const hasFluidOrNativeCandidate = sizes.includes("fluid");

  return (
    <div
      ref={rootRef}
      className={className ? `${styles.root} ${className}` : styles.root}
      data-gam-slot-root="true"
      data-gam-ad-unit={adUnit}
      data-gam-slot-id={instanceId}
      data-gam-rendered-height={renderedHeight ?? undefined}
      data-gam-min-height-mode={minHeight === "auto" ? "auto" : "reserved"}
      data-gam-fluid-native-candidate={
        hasFluidOrNativeCandidate ? "true" : undefined
      }
      style={cssVars}
      aria-label={AD_ARIA_LABEL}
    >
      <div id={instanceId} className={styles.slot} data-gam-slot="true">
        {!IS_PROD ? (
          <span className={styles.devLabel}>GAM slot: {adUnit}</span>
        ) : null}
      </div>
    </div>
  );
}